import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Trophy, Flag, Zap, Medal, Calendar } from 'lucide-react';

const API = '/api';

const F1Car = ({ racer, index, isLeader }) => {
  const position = racer.track_position * 100;
  const colors = ['#FF1801', '#00D2BE', '#0600EF', '#F596C8', '#FF8700', '#006F62', '#2B4562', '#B6BABD', '#469BFF', '#1B3D67'];
  const carColor = colors[index % colors.length];
  
  return (
    <div className="relative h-14 mb-2 group">
      <div className="absolute inset-0 bg-gradient-to-r from-gray-800/50 via-gray-700/30 to-gray-800/50 rounded-lg border border-gray-700/50">
        <div className="absolute inset-y-0 left-0 right-16 flex justify-between px-4">
          {[...Array(10)].map((_, i) => (
            <div key={i} className="w-0.5 h-full bg-gray-600/30" />
          ))}
        </div>
      </div>
      <div className="absolute right-0 top-0 bottom-0 w-4 bg-gradient-to-b from-white via-black to-white bg-[length:100%_8px] bg-repeat-y rounded-r-lg" />
      <div 
        className="absolute top-1/2 -translate-y-1/2 transition-all duration-1000 ease-out flex items-center"
        style={{ left: `calc(${Math.min(position, 95)}% - 40px)` }}
      >
        {racer.drs_active && (
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-green-500 text-xs px-2 py-0.5 rounded-full text-white font-bold animate-pulse">
            DRS
          </div>
        )}
        {(isLeader || racer.drs_active) && (
          <div className="absolute -left-8 top-1/2 -translate-y-1/2 flex flex-col gap-1">
            <div className="w-6 h-0.5 bg-gradient-to-r from-transparent to-yellow-400 rounded" />
            <div className="w-8 h-0.5 bg-gradient-to-r from-transparent to-yellow-400 rounded" />
            <div className="w-6 h-0.5 bg-gradient-to-r from-transparent to-yellow-400 rounded" />
          </div>
        )}
        <svg width="45" height="20" viewBox="0 0 50 20" className="drop-shadow-lg">
          <ellipse cx="25" cy="10" rx="20" ry="6" fill={carColor} />
          <ellipse cx="22" cy="10" rx="6" ry="4" fill="#1a1a1a" />
          <rect x="42" y="6" width="6" height="8" rx="1" fill={carColor} />
          <rect x="2" y="3" width="8" height="3" rx="1" fill={carColor} />
          <rect x="2" y="14" width="8" height="3" rx="1" fill={carColor} />
          <ellipse cx="38" cy="10" rx="4" ry="5" fill="#1a1a1a" />
          <ellipse cx="12" cy="10" rx="4" ry="5" fill="#1a1a1a" />
          <text x="25" y="13" textAnchor="middle" fill="white" fontSize="8" fontWeight="bold">{racer.position}</text>
        </svg>
        <div className="ml-2 bg-black/80 px-2 py-1 rounded text-xs font-medium whitespace-nowrap hidden group-hover:block">
          <div className="text-white">{racer.name}</div>
          <div className="text-gray-400">DPT: {racer.dpt.toLocaleString('da-DK')}</div>
        </div>
      </div>
      <div className={`absolute left-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
        racer.position === 1 ? 'bg-yellow-500 text-black' :
        racer.position === 2 ? 'bg-gray-300 text-black' :
        racer.position === 3 ? 'bg-amber-600 text-white' :
        'bg-gray-700 text-white'
      }`}>
        {racer.position}
      </div>
      <div className="absolute left-10 top-1/2 -translate-y-1/2 text-xs text-white/70 truncate max-w-24">
        {racer.name.split(' ')[0]}
      </div>
    </div>
  );
};

const Podium = ({ racers }) => {
  if (!racers || racers.length < 3) return null;
  const [first, second, third] = racers;
  return (
    <div className="flex items-end justify-center gap-2 h-48 mt-8">
      <div className="flex flex-col items-center">
        <div className="text-gray-300 text-sm mb-2 truncate max-w-20">{second?.name?.split(' ')[0]}</div>
        <div className="text-gray-400 text-xs mb-1">DPT: {second?.dpt?.toLocaleString('da-DK')}</div>
        <div className="w-20 h-24 bg-gradient-to-t from-gray-400 to-gray-300 rounded-t-lg flex items-center justify-center">
          <span className="text-4xl font-bold text-gray-700">2</span>
        </div>
      </div>
      <div className="flex flex-col items-center">
        <Trophy className="w-8 h-8 text-yellow-500 mb-1" />
        <div className="text-yellow-500 font-bold mb-1 truncate max-w-24">{first?.name?.split(' ')[0]}</div>
        <div className="text-yellow-400 text-xs mb-1">DPT: {first?.dpt?.toLocaleString('da-DK')}</div>
        <div className="w-24 h-32 bg-gradient-to-t from-yellow-600 to-yellow-400 rounded-t-lg flex items-center justify-center">
          <span className="text-5xl font-bold text-yellow-900">1</span>
        </div>
      </div>
      <div className="flex flex-col items-center">
        <div className="text-amber-600 text-sm mb-2 truncate max-w-20">{third?.name?.split(' ')[0]}</div>
        <div className="text-amber-500 text-xs mb-1">DPT: {third?.dpt?.toLocaleString('da-DK')}</div>
        <div className="w-20 h-20 bg-gradient-to-t from-amber-700 to-amber-500 rounded-t-lg flex items-center justify-center">
          <span className="text-4xl font-bold text-amber-900">3</span>
        </div>
      </div>
    </div>
  );
};

const Leaderboard = ({ racers, title }) => (
  <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700">
    <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
      <Flag className="w-5 h-5 text-red-500" />
      {title}
    </h3>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-gray-400 border-b border-gray-700">
            <th className="text-left py-2 px-2">Pos</th>
            <th className="text-left py-2">Konsulent</th>
            <th className="text-right py-2">DPT</th>
            <th className="text-right py-2">Timer</th>
            <th className="text-right py-2">DB</th>
            <th className="text-right py-2 px-2">Point</th>
          </tr>
        </thead>
        <tbody>
          {racers?.map((racer, idx) => (
            <tr key={idx} className={`border-b border-gray-800 ${
              racer.position === 1 ? 'bg-yellow-500/10' :
              racer.position === 2 ? 'bg-gray-400/10' :
              racer.position === 3 ? 'bg-amber-600/10' : ''
            }`}>
              <td className="py-2 px-2">
                <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                  racer.position === 1 ? 'bg-yellow-500 text-black' :
                  racer.position === 2 ? 'bg-gray-300 text-black' :
                  racer.position === 3 ? 'bg-amber-600 text-white' :
                  'bg-gray-700 text-white'
                }`}>{racer.position}</span>
              </td>
              <td className="py-2 text-white font-medium">{racer.name}</td>
              <td className="py-2 text-right text-green-400 font-mono">{racer.dpt?.toLocaleString('da-DK')}</td>
              <td className="py-2 text-right text-gray-400">{racer.hours}</td>
              <td className="py-2 text-right text-gray-400">{racer.db?.toLocaleString('da-DK')}</td>
              <td className="py-2 text-right px-2 font-bold text-white">{racer.points}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const ChampionshipStandings = ({ standings }) => (
  <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700">
    <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
      <Trophy className="w-5 h-5 text-yellow-500" />
      Mesterskabsstilling
    </h3>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-gray-400 border-b border-gray-700">
            <th className="text-left py-2 px-2">Pos</th>
            <th className="text-left py-2">Konsulent</th>
            <th className="text-right py-2">Point</th>
            <th className="text-right py-2">Sejre</th>
            <th className="text-right py-2">Podier</th>
            <th className="text-right py-2 px-2">Bedste DPT</th>
          </tr>
        </thead>
        <tbody>
          {standings?.map((s, idx) => (
            <tr key={idx} className={`border-b border-gray-800 ${
              s.position === 1 ? 'bg-yellow-500/10' :
              s.position === 2 ? 'bg-gray-400/10' :
              s.position === 3 ? 'bg-amber-600/10' : ''
            }`}>
              <td className="py-2 px-2">
                <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                  s.position === 1 ? 'bg-yellow-500 text-black' :
                  s.position === 2 ? 'bg-gray-300 text-black' :
                  s.position === 3 ? 'bg-amber-600 text-white' :
                  'bg-gray-700 text-white'
                }`}>{s.position}</span>
              </td>
              <td className="py-2 text-white font-medium">{s.name}</td>
              <td className="py-2 text-right text-yellow-400 font-bold">{s.total_points}</td>
              <td className="py-2 text-right text-gray-400">{s.wins}</td>
              <td className="py-2 text-right text-gray-400">{s.podiums}</td>
              <td className="py-2 text-right px-2 text-green-400">{s.best_dpt?.toLocaleString('da-DK')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const RaceCalendar = ({ races }) => (
  <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700">
    <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
      <Calendar className="w-5 h-5 text-blue-400" />
      Kalendar
    </h3>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
      {races?.map((race, idx) => (
        <div key={idx} className={`p-3 rounded-lg border ${
          race.status === 'active' ? 'bg-green-500/20 border-green-500' :
          race.status === 'completed' ? 'bg-gray-700/50 border-gray-600' :
          'bg-gray-800/50 border-gray-700'
        }`}>
          <div className="text-xs text-gray-400">{race.race_name}</div>
          <div className="text-sm text-white font-medium mt-1">
            {race.start_date?.split('-').reverse().join('/')} - {race.end_date?.split('-').reverse().slice(0, 2).join('/')}
          </div>
          {race.status === 'active' && (
            <div className="text-xs text-green-400 mt-1 flex items-center gap-1">
              <Zap className="w-3 h-3" /> LIVE
            </div>
          )}
          {race.status === 'completed' && race.winner && (
            <div className="text-xs text-yellow-400 mt-1">Vinder: {race.winner}</div>
          )}
        </div>
      ))}
    </div>
  </div>
);

const F1Public = () => {
  const { token } = useParams();
  const [raceData, setRaceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/f1/public/${token}`);
      setRaceData(response.data);
      setError(null);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Linket er ugyldigt eller udløbet.');
      } else {
        setError('Kunne ikke hente data.');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading && !raceData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Indlæser F1 data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-400 text-xl mb-2">{error}</div>
          <p className="text-gray-500 text-sm">Kontakt din administrator for et nyt link.</p>
        </div>
      </div>
    );
  }

  const currentRace = raceData?.current_race;
  const racers = currentRace?.racers || [];
  const standings = raceData?.championship_standings || [];
  const calendar = raceData?.race_calendar || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900" data-testid="f1-public-page">
      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-5 mb-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_b471bfc1-d520-45f4-877f-1cd6fc8ad74a/artifacts/dus55wts_F1%20logo.png"
              alt="F1 Logo"
              className="h-16 md:h-20 w-auto object-contain"
              data-testid="f1-public-logo"
            />
            <div className="h-12 w-px bg-gradient-to-b from-transparent via-red-500 to-transparent" />
            <h1 className="text-3xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-white to-red-500">
              GARDINBUSSEN CHAMPIONSHIP
            </h1>
          </div>
          <p className="text-gray-400">
            {raceData?.competition?.start_date?.split('-').reverse().join('/')} - {raceData?.competition?.end_date?.split('-').reverse().join('/')}
          </p>
        </div>

        {currentRace && (
          <div className="bg-gradient-to-r from-red-900/50 via-gray-900/50 to-red-900/50 rounded-xl p-4 mb-6 border border-red-500/30">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="text-red-400 text-sm font-medium uppercase tracking-wider">
                  {currentRace.status === 'active' ? 'LIVE RACE' : currentRace.status === 'completed' ? 'AFSLUTTET' : 'KOMMENDE'}
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-white">{currentRace.race_name}</h2>
                <p className="text-gray-400">
                  {currentRace.start_date?.split('-').reverse().join('/')} - {currentRace.end_date?.split('-').reverse().join('/')}
                </p>
              </div>
              {currentRace.leader && (
                <div className="text-right">
                  <div className="text-gray-400 text-sm">Aktuel fører</div>
                  <div className="text-2xl font-bold text-yellow-400">{currentRace.leader.name}</div>
                  <div className="text-green-400">DPT: {currentRace.leader.dpt?.toLocaleString('da-DK')}</div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="bg-gray-900/50 rounded-xl p-6 mb-6 border border-gray-700">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Flag className="w-6 h-6 text-red-500" />
            Ugens Løb - Live Track
          </h3>
          <div className="relative">
            <div className="absolute -top-2 right-0 bg-black text-white px-3 py-1 text-xs font-bold rounded">MÅL</div>
            <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
              {racers.slice(0, 12).map((racer, idx) => (
                <F1Car key={racer.name} racer={racer} index={idx} isLeader={idx === 0} />
              ))}
            </div>
          </div>
        </div>

        {currentRace?.status === 'completed' && racers.length >= 3 && (
          <div className="bg-gradient-to-b from-gray-900/50 to-black/50 rounded-xl p-6 mb-6 border border-yellow-500/30">
            <h3 className="text-xl font-bold text-white text-center mb-2 flex items-center justify-center gap-2">
              <Medal className="w-6 h-6 text-yellow-500" />
              Ugens Podium
            </h3>
            <Podium racers={racers.slice(0, 3)} />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <Leaderboard racers={racers} title="Ugens Stilling" />
          <ChampionshipStandings standings={standings} />
        </div>

        <RaceCalendar races={calendar} />

        <div className="text-center text-gray-500 text-sm mt-8">
          Sidst opdateret: {raceData?.last_updated ? new Date(raceData.last_updated).toLocaleString('da-DK') : '-'}
        </div>
      </main>
    </div>
  );
};

export default F1Public;
